import { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { login, sendPasswordReset } from '../../../services/authService'

const Login = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [capsLockOn, setCapsLockOn] = useState(false)

  const emailInputRef = useRef(null)
  const passwordInputRef = useRef(null)
  const resetEmailRef = useRef(null)

  useEffect(() => {
    const savedRemember = localStorage.getItem('psyber-remember-me') === 'true'
    const savedEmail = localStorage.getItem('psyber-remember-email')

    if (savedRemember && savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }))
      setRememberMe(true)
    }
  }, [])

  useEffect(() => {
    if (showForgotPassword) {
      resetEmailRef.current?.focus()
    } else {
      emailInputRef.current?.focus()
    }
  }, [showForgotPassword])

  useEffect(() => {
    if (showForgotPassword) {
      setPasswordVisible(false)
      setCapsLockOn(false)
    }
  }, [showForgotPassword])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('') // Clear error when user types
  }

  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked
    setRememberMe(isChecked)
    if (!isChecked) {
      localStorage.removeItem('psyber-remember-me')
      localStorage.removeItem('psyber-remember-email')
    }
  }

  const handlePasswordToggle = () => {
    setPasswordVisible((prev) => !prev)
    passwordInputRef.current?.focus()
  }

  const handleCapsLock = (event) => {
    setCapsLockOn(event.getModifierState('CapsLock'))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await login(formData.email, formData.password)

    setLoading(false)

    if (result.success) {
      const userData = result.user

      if (rememberMe) {
        localStorage.setItem('psyber-remember-me', 'true')
        localStorage.setItem('psyber-remember-email', formData.email)
      } else {
        localStorage.removeItem('psyber-remember-me')
        localStorage.removeItem('psyber-remember-email')
      }

      // Check if password change is required for new employees
      if (userData?.passwordChangeRequired === true) {
        navigate('/change-password', {
          state: {
            forced: true,
            message: 'Please change your temporary password to continue'
          }
        })
      } else {
        navigate('/dashboard')
      }
    } else {
      setError(result.error)
      const normalizedError = result.error?.toLowerCase() ?? ''
      if (normalizedError.includes('password')) {
        passwordInputRef.current?.focus()
      } else {
        emailInputRef.current?.focus()
      }
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')
    setLoading(true)

    const result = await sendPasswordReset(resetEmail)

    setLoading(false)

    if (result.success) {
      setSuccessMessage('Password reset email sent! Check your inbox.')
      setResetEmail('')
      // Auto-switch back to login after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false)
        setSuccessMessage('')
      }, 3000)
    } else {
      setError(result.error)
    }
  }

  const handleBackToLogin = () => {
    setShowForgotPassword(false)
    setError('')
    setSuccessMessage('')
    setResetEmail('')
  }

  const hasError = Boolean(error)
  const errorMessageId = hasError ? 'login-form-error' : undefined
  const successMessageId = successMessage ? 'login-success-message' : undefined
  const passwordCapsLockId = capsLockOn ? 'password-caps-lock' : undefined

  return (
    <div className="psyber-container psyber-portal-container">
      {/* Background Effects */}
      <div className="psyber-bg-effects">
        <div className="psyber-orb psyber-orb-1"></div>
        <div className="psyber-orb psyber-orb-2"></div>
        <div className="psyber-grid"></div>
      </div>

      {/* Login Card */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh'
      }}>
        <div className="psyber-card" style={{
          maxWidth: '450px',
          width: '100%',
          textAlign: 'center'
        }}>
          {/* Logo/Title */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="psyber-title-gradient" style={{ marginBottom: '0.5rem' }}>
              {showForgotPassword ? 'Reset Password' : 'PsyberNova Portal'}
            </h1>
            <p style={{ color: 'var(--psyber-text-secondary)' }}>
              {showForgotPassword ? 'Enter your email to receive reset instructions' : 'Employee Intranet Portal'}
            </p>
          </div>

          {/* Login Form */}
          <form
            onSubmit={showForgotPassword ? handleForgotPassword : handleSubmit}
            style={{ textAlign: 'left' }}
            aria-busy={loading}
          >
            {/* Error Message */}
            {error && (
              <div
                style={{
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  background: 'var(--psyber-error-bg)',
                  color: 'var(--psyber-error)',
                  borderRadius: 'var(--psyber-radius-md)',
                  border: '1px solid var(--psyber-error)',
                  fontSize: 'var(--psyber-font-size-sm)'
                }}
                id="login-form-error"
                role="alert"
                aria-live="assertive"
              >
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div
                style={{
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  background: 'var(--psyber-success-bg)',
                  color: 'var(--psyber-success)',
                  borderRadius: 'var(--psyber-radius-md)',
                  border: '1px solid var(--psyber-success)',
                  fontSize: 'var(--psyber-font-size-sm)'
                }}
                id="login-success-message"
                role="status"
                aria-live="polite"
              >
                <i className="fas fa-check-circle"></i> {successMessage}
              </div>
            )}

            {/* Email Input */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="email" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="psyber-input"
                placeholder="your.email@company.com"
                value={showForgotPassword ? resetEmail : formData.email}
                onChange={showForgotPassword
                  ? (e) => setResetEmail(e.target.value)
                  : handleChange
                }
                required
                autoComplete="email"
                aria-invalid={hasError}
                aria-describedby={[errorMessageId, successMessageId].filter(Boolean).join(' ') || undefined}
                ref={showForgotPassword ? resetEmailRef : emailInputRef}
              />
            </div>

            {/* Password Input - Only show in login mode */}
            {!showForgotPassword && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="password" style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 600,
                  color: 'var(--psyber-text)'
                }}>
                  Password
                </label>
                <div className="psyber-input-wrapper">
                  <input
                    type={passwordVisible ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className="psyber-input"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    onKeyUp={handleCapsLock}
                    onKeyDown={handleCapsLock}
                    onBlur={() => setCapsLockOn(false)}
                    required
                    autoComplete="current-password"
                    aria-invalid={hasError}
                    aria-describedby={[errorMessageId, passwordCapsLockId].filter(Boolean).join(' ') || undefined}
                    ref={passwordInputRef}
                  />
                  <button
                    type="button"
                    className="psyber-input-action"
                    onClick={handlePasswordToggle}
                    aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                    aria-pressed={passwordVisible}
                  >
                    <i className={passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
                {capsLockOn && (
                  <p
                    id="password-caps-lock"
                    style={{
                      marginTop: '0.75rem',
                      fontSize: 'var(--psyber-font-size-sm)',
                      color: 'var(--psyber-warning)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <i className="fas fa-exclamation-triangle"></i> Caps lock is on
                  </p>
                )}
              </div>
            )}

            {/* Forgot Password Link - Only show in login mode */}
            {!showForgotPassword && (
              <div style={{
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: 'var(--psyber-font-size-sm)',
                  color: 'var(--psyber-text-secondary)'
                }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    style={{ width: '16px', height: '16px' }}
                    aria-label="Remember this device"
                  />
                  Remember this device
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--psyber-primary)',
                    fontSize: 'var(--psyber-font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    padding: 0
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  <i className="fas fa-key"></i> Forgot password?
                </button>
              </div>
            )}

            {/* Back to Login Link - Only show in forgot password mode */}
            {showForgotPassword && (
              <div style={{
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--psyber-primary)',
                    fontSize: 'var(--psyber-font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    padding: 0
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  <i className="fas fa-arrow-left"></i> Back to login
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="psyber-btn psyber-btn-primary"
              disabled={loading}
              style={{ width: '100%' }}
              aria-describedby={[errorMessageId, successMessageId].filter(Boolean).join(' ') || undefined}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {showForgotPassword ? 'Sending...' : 'Signing in...'}
                </>
              ) : (
                <>
                  {showForgotPassword ? (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Send Reset Email
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt"></i>
                      Sign In
                    </>
                  )}
                </>
              )}
            </button>
          </form>

          {/* Footer Links - Only show in login mode */}
          {!showForgotPassword && (
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid var(--psyber-card-border)',
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)'
            }}>
              <p>
                First time setup?{' '}
                <Link to="/register-admin" style={{
                  color: 'var(--psyber-primary)',
                  fontWeight: 600
                }}>
                  Register Super Admin
                </Link>
              </p>
              <p style={{ marginTop: '0.75rem' }}>
                Need help signing in? Reach out to your portal administrator for access support.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Login
